import {
  Alert,
  AlertIcon,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';
import InputNumber from 'components/InputNumber';
import PixKeyInput from 'components/PixKeyInput';
import useAuth from 'contexts/useAuth';
import React from 'react';
import { useForm } from 'react-hook-form';
import { createWithdrawal, validatePixKey } from 'services/api.service';
import { maskCurrency } from 'utils/number';
import { unMaskPixKey } from 'utils/pix';
import { z } from 'zod';
import { useTwoFaHelper } from 'contexts/TwoFaCheckContext';
import CustomButton from 'components/CustomButton';
import { ConsultPixKey } from '../types/QRcode/qrcode.type';
import { queryClient } from 'index';

export const chaveSchema = z.union([
  z.string().regex(/^\d+$/).length(11),
  z.string().regex(/^\d+$/).length(14),
  z.string().regex(/^\+[1-9]\d\d{1,14}$/),
  z
    .string()
    .regex(
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/,
    ),
  z
    .string()
    .regex(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/)
    .length(36),
]);

const WithdrawPix: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const toast = useToast();
  const { userData, updateUserData } = useAuth();
  const [step, setStep] = React.useState<number>(0);
  const [loading, setLoading] = React.useState(false);
  const [pixKey, setPixKey] = React.useState<ConsultPixKey | undefined>();
  const [typePix, setTypePix] = React.useState<'CPF' | 'PHONE' | null>(null);

  const { mutateAsync: consultPixKey } = useMutation(['consultPixKey'], async (keyPix: string) =>
    validatePixKey(keyPix),
  );

  const {
    control,
    handleSubmit,
    register,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<{
    keyPix: string;
    amount: number;
    description: string;
  }>();

  const { requestTwoFa } = useTwoFaHelper();

  const WithdrawPix = async (data: any) => {
    const code = await requestTwoFa();
    if (!code?.id) {
      return;
    }

    setLoading(true);
    const pix = unMaskPixKey(data.keyPix, typePix);

    try {
      await createWithdrawal(code.id, {
        ...data,
        type: 'DICT',
        keyPix: pix.raw,
        endToEndId: pixKey?.endToEndId,
        integrationId: pixKey?.integrationId,
        //accountHolderDocument: pixKey?.document,
        accountHolderName: pixKey?.name,
      });
      updateUserData();
      toast({
        title: 'Sucesso',
        description: 'Saque criado com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      queryClient.refetchQueries({ queryKey: ['findAllWithdrawers'] });
      onClose();
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error?.response?.data?.message || 'Erro ao criar saque',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleValidatePixKey = async (value: string) => {
    const pix = unMaskPixKey(value);
    setLoading(true);

    try {
      const res = await consultPixKey(pix.raw);
      setStep(1);
      toast({
        title: 'Sucesso',
        description: 'Chave pix válida',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      setPixKey(res);
    } catch (err) {
      toast({
        title: 'Erro',
        description: 'Não foi possível validar a chave pix',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal
        isOpen={true}
        onClose={onClose}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Saque Pix</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack
              spacing={5}
              alignItems="start"
            >
              {step === 0 && (
                <>
                  <PixKeyInput
                    label="Chave Pix"
                    control={control}
                    setPixTypeKey={setTypePix}
                    placeholder="Chave pix"
                    name="keyPix"
                    registerOptions={{
                      required: 'Campo obrigatório',
                      validate: {
                        valid: (value) => {
                          return unMaskPixKey(value).type !== 'INVALID' || 'Chave pix inválida';
                        },
                      },
                    }}
                    type="text"
                    setValue={setValue}
                  />
                  <InputNumber
                    control={control}
                    percentage={false}
                    placeholder="R$ 0,00"
                    label={'Valor'}
                    name={`amount`}
                    rules={{
                      required: 'Campo obrigatório',
                      validate: {
                        positive: (value) => value > 0 || 'O valor deve ser maior que 0',
                        max: (value) =>
                          value <= +userData.balance - +userData.amountLocked ||
                          'O valor deve ser menor que o saldo disponível',
                      },
                    }}
                  />

                  <Text>
                    Saldo Disponível: <b>{maskCurrency(userData.balance - +userData.amountLocked)}</b>
                  </Text>
                  <FormControl isInvalid={!!errors.description?.message}>
                    <FormLabel
                      display="flex"
                      ms="4px"
                      fontSize="sm"
                      mb="8px"
                    >
                      Descrição<Text>*</Text>
                    </FormLabel>
                    <Input
                      isRequired={true}
                      variant="auth"
                      fontSize="sm"
                      size="sm"
                      {...register('description', {
                        maxLength: {
                          value: 100,
                          message: 'O limite de caracteres é de 100',
                        },
                      })}
                      ms={{ base: '0px', md: '0px' }}
                      type="text"
                      rounded="sm"
                      placeholder="Pagamento de contas"
                    />
                    <FormErrorMessage>{errors.description?.message}</FormErrorMessage>
                  </FormControl>
                </>
              )}
              {step === 1 && (
                <>
                  {pixKey && step === 1 && (
                    <VStack
                      w="full"
                      p={4}
                      bg="gray.50"
                      borderRadius="md"
                      border="1px"
                      borderColor="gray.200"
                      spacing={3}
                      align="stretch"
                    >
                      <Text
                        fontSize="lg"
                        fontWeight="bold"
                      >
                        Destinatário
                      </Text>
                      <HStack justify="space-between">
                        <Text
                          fontWeight="medium"
                          color="gray.600"
                        >
                          Nome
                        </Text>
                        <Text>{pixKey.name}</Text>
                      </HStack>
                      <HStack justify="space-between">
                        <Text
                          fontWeight="medium"
                          color="gray.600"
                        >
                          Chave Pix
                        </Text>
                        <Text>{pixKey.key}</Text>
                      </HStack>
                      <HStack justify="space-between">
                        <Text
                          fontWeight="medium"
                          color="gray.600"
                        >
                          Documento
                        </Text>
                        <Text>{pixKey.document}</Text>
                      </HStack>
                    </VStack>
                  )}
                  {pixKey && step === 1 && (
                    <Alert status="info" borderRadius="md">
                      <AlertIcon />
                      <Text fontSize="sm">
                        Data da última busca da chave: {new Date(pixKey.lastModified).toLocaleString('pt-BR')}
                      </Text>
                    </Alert>
                  )}
                  {step === 1 && (
                    <>
                      <VStack
                        w="full"
                        spacing={2}
                        align="center"
                        py={4}
                      >
                        <Text color="gray.600">Valor solicitado</Text>
                        <Text
                          fontSize="2xl"
                          fontWeight="bold"
                          color="#2D9CDB"
                        >
                          {maskCurrency(getValues('amount'))}
                        </Text>
                        <Text color="gray.600">
                          Descrição: <b>{getValues('description')}</b>
                        </Text>
                      </VStack>
                    </>
                  )}
                </>
              )}
            </VStack>
          </ModalBody>

          <ModalFooter>
            <HStack spacing={4}>
              {step === 1 && (
                <CustomButton
                  colorScheme=""
                  isLoading={loading}
                  onClick={() => setStep(0)}
                >
                  Voltar
                </CustomButton>
              )}
              <CustomButton
                colorScheme="green"
                isLoading={loading}
                onClick={step === 1 ? handleSubmit(WithdrawPix) : () => handleValidatePixKey(getValues('keyPix'))}
              >
                {step === 1 ? 'Criar saque' : 'Validar chave pix'}
              </CustomButton>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default WithdrawPix;
